import React, { Component } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import {
  Tooltip,
  TooltipProvider,
  TooltipTrigger,
  TooltipContent,
} from '@cargomatic/ui';
import { Info } from 'lucide-react';
import _ from 'underscore';
import { humanize, capitalize, titleize } from 'underscore.string';
import CargomaticCollapsibleCard from 'components/v2/CollapsibleCard';
import DescriptionListItem from 'components/v2/DescriptionListItem';
import ItemAlt from 'components/ui/tailwind/ItemAlt';
import OrderWebAPIUtils from 'utils/OrderWebAPIUtils';
import { withTranslation } from 'react-i18next';
import {
  weightUnitsOptions,
  temperatureUnitsOptions,
} from 'utils/localization/types';
import OrdersActionCreators from 'actions/OrdersActionCreators';

const serviceTypes = [
  {
    localizationSlug: 'LIVE_LOAD_UNLOAD',
    name: 'Live Load/Unload',
    value: 'live',
  },
  {
    localizationSlug: 'DROP_HOOK',
    name: 'Drop & Hook',
    value: 'drop',
  },
];

const movementTypes = [
  {
    localizationSlug: 'PICKUP',
    name: 'Pickup',
    value: 'pickup',
  },
  {
    localizationSlug: 'DELIVERY',
    name: 'Delivery',
    value: 'delivery',
  },
];

const orderTypes = [
  {
    localizationSlug: 'IMPORT',
    name: 'Import',
    value: 'import',
  },
  {
    localizationSlug: 'EXPORT',
    name: 'Export',
    value: 'export',
  },
];

const modeTypes = [
  {
    localizationSlug: 'DRAYAGE',
    name: 'Drayage',
    value: 'drayage',
  },
  {
    localizationSlug: 'INTERMODAL',
    name: 'Intermodal',
    value: 'intermodal',
  },
];

function formatDate(date, t) {
  return date ? moment(date).format('L') : t('NOT_SET');
}

class InboundDetails extends Component {
  constructor(props) {
    super(props);
    this.state = { railroads: this.props?.order?.railroads };
  }

  componentDidMount() {
    this.retrieveOceanScacs();
    this.retriveRailRoadItems();
  }

  retriveRailRoadItems() {
    const railRoadOptions = [
      { label: 'BNSF', value: 'BNSF' },
      { label: 'CN', value: 'CN' },
      { label: 'CPKC', value: 'CPKC' },
      { label: 'CSX', value: 'CSX' },
      { label: 'FCCM', value: 'FCCM' },
      { label: 'FEC', value: 'FEC' },
      { label: 'FSR', value: 'FSR' },
      { label: 'FXE', value: 'FXE' },
      { label: 'NDM', value: 'NDM' },
      { label: 'NS', value: 'NS' },
      { label: 'UP', value: 'UP' },
      { label: 'UTFM', value: 'UTFM' },
    ];
    this.setState({ railRoadItems: railRoadOptions });
  }

  async retrieveOceanScacs() {
    const ocean_scacs = await OrderWebAPIUtils.getCarrierScacs();
    if (ocean_scacs) {
      this.setState({
        ocean_scacs: ocean_scacs.OCEAN_SCAC_CODES.map((scac) => ({
          name: scac.carrier,
          value: scac.ocean_scac,
        })),
      });
    }
  }

  handleOnChangeRailRoadMultiSelect = (selected) => {
    if (Array.isArray(selected)) {
      this.setState({ railroads: selected?.map(({ value }) => value) });
    }
  };

  handleOnChangeCustomRailRoadOption = (value) => {
    this.setState((prev) => ({
      railRoadItems: [...prev.railRoadItems, { value, label: value }],
    }));
  };

  renderMultiSelectField(
    field,
    value,
    label,
    items,
    placeholderText,
    onChangeMultiSelect,
    allowCustom,
    onChangeCustom,
    customOptionLabel,
    addCustomLabel,
  ) {
    const { t } = this.props;
    return (
      <>
        <DescriptionListItem label={t(label)} dataTestId={field}>
          <ItemAlt
            name={field}
            type="multi-select"
            menuItems={items}
            value={value}
            displayValue={value?.join(',')}
            displayClass="max-w-[140px] break-words"
            editable
            onValueSave={this.props.onEdit}
            onValueCancel={() => {
              this.setState({ railroads: this.props?.order?.railroads });
            }}
            placeholderText={placeholderText}
            allowCustom={allowCustom}
            onChange={onChangeMultiSelect}
            customOptionLabel={customOptionLabel}
            addCustomLabel={addCustomLabel}
            onCustomValueAdd={onChangeCustom}
          />
        </DescriptionListItem>
      </>
    );
  }

  renderDropdownField(field, value, label, items, displayFunc, type) {
    const { t } = this.props;
    return (
      <DescriptionListItem label={t(label)} className="mb-3" dataTestId={field}>
        <ItemAlt
          name={field}
          type={type || 'dropdown'}
          menuItems={items}
          localizationKey="localizationSlug"
          displayValue={displayFunc}
          value={value}
          editable
          onValueSave={this.props.onEdit}
          glyphClass="float-left cursor-pointer mb-1"
        />
      </DescriptionListItem>
    );
  }

  renderDropdownWithInput(
    field,
    value,
    label,
    items,
    displayValue,
    renderConditionalField = true,
  ) {
    const { t } = this.props;
    return (
      <DescriptionListItem label={t(label)} className="mb-3" dataTestId={field}>
        <ItemAlt
          name={field}
          type="dropdown_with_input"
          menuItems={items}
          displayValue={displayValue}
          value={value}
          renderConditionalField={renderConditionalField}
          editable
          onValueSave={this.props.onEdit}
          glyphClass="float-left cursor-pointer mb-1"
        />
      </DescriptionListItem>
    );
  }

  renderField(field, type, label, formatter) {
    const { order, t } = this.props;
    let displayValue = null;
    if (field === 'reefer_temperature' && Number.isNaN(order[field])) {
      formatter = null;
    }
    if (type === 'date') {
      displayValue = formatDate(order[field], t);
    } else if (formatter) {
      displayValue = formatter(order[field]);
    }

    let maxLength;
    if (field === 'special_instructions') maxLength = 500;

    return (
      <DescriptionListItem
        label={titleize(t(label, { count: 1 }))}
        className="mb-3"
        dataTestId={field}
      >
        <ItemAlt
          name={field}
          type={type}
          value={order[field]}
          displayValue={displayValue}
          editable
          onValueSave={this.props.onEdit}
          glyphClass="float-left cursor-pointer mb-1"
          saveButtonClass="button button-green"
          maxLength={maxLength}
        />
      </DescriptionListItem>
    );
  }

  renderExportDateFields() {
    const {
      order: { order_type },
    } = this.props;

    if (order_type !== 'export') {
      return null;
    }
    return (
      <div>
        {this.renderField('last_free_date', 'date', 'LAST_CUT_DATE')}
        {this.renderField(
          'earliest_return_date',
          'date',
          'EARLIEST_RETURN_DATE',
        )}
        {this.renderField(
          'earliest_empty',
          'date',
          'EARLIEST_DATE_TO_GET_EMPTY',
        )}
      </div>
    );
  }

  renderImportDateFields() {
    const {
      order: { order_type },
    } = this.props;

    if (order_type !== 'import') {
      return null;
    }

    return (
      <>
        {this.renderField('last_free_date', 'date', 'LAST_FREE_DATE')}
        {this.renderField('arrival_date', 'date', 'ARRIVAL_DATE')}
        {this.renderField(
          'estimated_arrival',
          'date',
          'ESTIMATED_ARRIVAL_DATE',
        )}
      </>
    );
  }

  getCustomScacData(order) {
    const { ocean_scacs } = this.state;
    const { ocean_carrier_scac } = order;
    const scacKeys = (ocean_scacs || []).map((scac) => scac.name);
    const renderScacInput =
      (ocean_carrier_scac !== undefined || ocean_carrier_scac !== '') &&
      !scacKeys.includes(ocean_carrier_scac);
    const customScacInput = (ocean_scacs || []).find(
      (scac) => scac.value === ocean_carrier_scac,
    );

    return {
      renderScacInput,
      customScacValue: !_.isEmpty(customScacInput) ? customScacInput.value : '',
      customScacInput:
        !_.isEmpty(customScacInput) && customScacInput.name !== 'Custom'
          ? customScacInput.name
          : ocean_carrier_scac,
    };
  }

  render() {
    const order = this.props.order;
    const originating =
      order?.originating && order?.originating.length > 0
        ? order?.originating[0].address
        : '';

    const modeDisabled =
      order?.shipments.length > 0 &&
      order?.shipments.some((shipment) => shipment.shipment_type === 'rail');

    const scacData = this.getCustomScacData(order);
    const { railroads, railRoadItems } = this.state;
    const { t } = this.props;
    return (
      <CargomaticCollapsibleCard
        id="order-details"
        header={() => <span>{t('ORDER_DETAILS')}</span>}
      >
        <div className="grid grid-cols-2 grid-gap-6">
          <DescriptionListItem
            label={t('ORDER_TYPE')}
            className="mb-3"
            dataTestId="order-type-kv"
          >
            <ItemAlt
              name="order_type"
              type="dropdown"
              menuItems={orderTypes}
              localizationKey="localizationSlug"
              displayValue={t(order?.order_type?.toUpperCase())}
              value={order?.order_type}
              editable={false}
              onValueSave={this.props.onEdit}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          <DescriptionListItem
            label={titleize(t('MODE'))}
            className="mb-3"
            dataTestId="order-mode-kv"
          >
            {modeDisabled && (
              <div className="">
                <span className="mr-2">{capitalize(order?.mode || '')}</span>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span className="inline-block">
                        <Info className="h-4 w-4" />
                      </span>
                    </TooltipTrigger>
                    <TooltipContent side="top">
                      {t('EDIT_MODE_DISABLED_TIP')}
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            )}
            {!modeDisabled && (
              <ItemAlt
                name="mode"
                type="dropdown"
                menuItems={modeTypes}
                localizationKey="localizationSlug"
                displayValue={t(order?.mode?.toUpperCase())}
                value={order?.mode}
                editable
                onValueSave={this.props.onEdit}
                glyphClass="float-left cursor-pointer mb-1"
              />
            )}
          </DescriptionListItem>
          <DescriptionListItem
            label={t('SERVICE_TYPE')}
            className="mb-3"
            dataTestId="order-service-type-kv"
          >
            <ItemAlt
              name="service_type"
              type="dropdown"
              localizationKey="localizationSlug"
              menuItems={serviceTypes}
              displayValue={t(
                _.findWhere(serviceTypes, {
                  value: order?.service_type,
                })?.localizationSlug,
              )}
              value={order?.service_type}
              editable
              onValueSave={this.props.onEdit}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          <DescriptionListItem
            label={t('MOVEMENT_TYPE')}
            className="mb-3"
            dataTestId="order-movement-type-kv"
          >
            <ItemAlt
              name="movement_type"
              type="dropdown"
              localizationKey="localizationSlug"
              menuItems={movementTypes}
              displayValue={humanize(order?.movement_type)}
              value={order?.movement_type}
              editable
              onValueSave={this.props.onEdit}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          {this.renderField('total_weight', 'number', 'TOTAL_WEIGHT')}
          {this.renderField('certified_weight', 'number', 'CERTIFIED_WEIGHT')}
          <DescriptionListItem
            label={t('WEIGHT_UNIT')}
            className="mb-3"
            dataTestId="order-weight-unit-kv"
          >
            <ItemAlt
              name="weight_units"
              type="dropdown"
              localizationKey="localizationSlug"
              menuItems={weightUnitsOptions}
              displayValue={order?.weight_units}
              value={order?.weight_units}
              editable
              onValueSave={this.props.onEdit}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          {this.renderField('count', 'number', 'COUNT')}
          {this.renderField(
            'reefer_temperature',
            'string',
            'REEFER_TEMPERATURE',
          )}
          <DescriptionListItem
            label={t('REEFER_TEMPERATURE_UNIT')}
            className="mb-3"
            dataTestId="reefer-temperature-kv"
          >
            <ItemAlt
              name="reefer_temperature_unit"
              type="dropdown"
              localizationKey="localizationSlug"
              disableTitleize
              menuItems={temperatureUnitsOptions}
              displayValue={t(
                _.findWhere(temperatureUnitsOptions, {
                  value: order?.reefer_temperature_unit,
                })?.localizationSlug,
              )}
              value={order?.reefer_temperature_unit}
              editable
              onValueSave={this.props.onEdit}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          {this.renderField(
            'beneficial_cargo_owner',
            'string',
            'BENEFICIAL_CARGO_OWNER',
          )}
          <DescriptionListItem
            label={t('ORIGINATING')}
            className="mb-3"
            dataTestId="order-originating-kv"
          >
            <ItemAlt
              name="originating"
              type="address"
              value={originating || ''}
              editable
              onValueSave={(loc) => {
                const update = {
                  address: loc.originating,
                };
                OrdersActionCreators.updateOrder(order?._id, {
                  originating: update,
                });
              }}
              glyphClass="float-left cursor-pointer mb-1"
            />
          </DescriptionListItem>
          <div>
            {this.state.ocean_scacs &&
              this.state.ocean_scacs.length &&
              this.renderDropdownWithInput(
                'ocean_carrier_scac',
                scacData.customScacValue,
                'STEAMSHIP',
                this.state.ocean_scacs,
                scacData.customScacInput,
                scacData.renderScacInput,
              )}
          </div>
          <div>
            {this.renderMultiSelectField(
              'railroads',
              railroads,
              t('RAILROADS'),
              railRoadItems,
              t('RAILROADS'),
              this.handleOnChangeRailRoadMultiSelect,
              true,
              this.handleOnChangeCustomRailRoadOption,
              t('CUSTOM_RAILROADS'),
              t('ADD_CUSTOM_RAILROAD'),
            )}
          </div>

          {this.renderField('vessel_or_rail', 'string', 'VESSEL')}
          {this.renderField('train', 'string', 'TRAIN')}
          {this.renderImportDateFields()}
          {this.renderExportDateFields()}
          {this.renderField(
            'special_instructions',
            'string',
            'SPECIAL_INSTRUCTIONS',
          )}
          {this.renderField('cargo_description', 'string', 'CARGO_DESCRIPTION')}
        </div>
      </CargomaticCollapsibleCard>
    );
  }
}

InboundDetails.propTypes = {
  order: PropTypes.object.isRequired,
  onEdit: PropTypes.func.isRequired,
};

export default withTranslation()(InboundDetails);
